import {SignatureV4} from '@smithy/signature-v4';
import {HttpRequest} from '@smithy/protocol-http';
import {Sha256} from '@aws-crypto/sha256-js';
import {Credentials} from 'aws-sdk';
import {ChatTransport, UIMessageChunk} from 'ai';
import type {UIMessage} from '@ai-sdk/react';

let cachedBaseUrl = '';

async function getBaseUrl(): Promise<string> {
    if (cachedBaseUrl) return cachedBaseUrl;
    const res = await fetch(`${window.location.origin}/settings.json`);
    const settings = await res.json();
    cachedBaseUrl = settings.NA?.cassandraEndpoint ?? '';
    return cachedBaseUrl;
}

let userId = 'portal-user';
let getCognitoIdToken: () => Promise<string> = async () => '';

export function setUserId(id: string) {
    userId = id;
}

export function setCognitoIdTokenProvider(provider: () => Promise<string>) {
    getCognitoIdToken = provider;
}

async function signedFetch(url: string, body: string, credentials: Credentials, signal?: AbortSignal): Promise<Response> {
    const parsed = new URL(url);
    const signer = new SignatureV4({
        credentials: {
            accessKeyId: credentials.accessKeyId,
            secretAccessKey: credentials.secretAccessKey,
            sessionToken: credentials.sessionToken,
        },
        region: 'us-east-1',
        service: 'lambda',
        sha256: Sha256,
    });

    const request = new HttpRequest({
        method: 'POST',
        protocol: parsed.protocol,
        hostname: parsed.hostname,
        path: parsed.pathname,
        headers: {
            'Content-Type': 'application/json',
            host: parsed.hostname,
        },
        body,
    });

    const signed = await signer.sign(request);
    let idToken = '';
    try {
        idToken = await getCognitoIdToken();
    } catch (e) {
        console.warn('Failed to fetch Cognito ID token; proceeding without it', e);
    }

    return fetch(url, {
        method: 'POST',
        headers: {
            ...(signed.headers as Record<string, string>),
            'Amz-Mons-Idp-Subject': userId,
            ...(idToken ? {'X-Cognito-Id-Token': idToken} : {}),
        },
        body,
        signal,
    });
}

function parseORPCStream(response: Response): ReadableStream<UIMessageChunk> {
    if (!response.body) {
        throw new Error('Response body is null');
    }
    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';

    return new ReadableStream<UIMessageChunk>({
        async start(controller) {
            try {
                let done = false;
                while (!done) {
                    const result = await reader.read();
                    done = result.done;
                    if (result.value) {
                        buffer += decoder.decode(result.value, {stream: true});
                        const lines = buffer.split('\n');
                        buffer = lines.pop() ?? '';
                        for (const line of lines) {
                            if (!line.startsWith('data: ')) continue;
                            try {
                                const parsed = JSON.parse(line.slice(6));
                                const chunk = parsed.json ?? parsed;
                                controller.enqueue(chunk as UIMessageChunk);
                            } catch {
                                /* skip malformed lines */
                            }
                        }
                    }
                }
                controller.close();
            } catch (err) {
                controller.error(err);
            }
        },
        cancel() {
            reader.cancel();
        },
    });
}

export interface Chat {
    id: string;
    title: string;
    createdAt: string;
    updatedAt: string;
}

export async function listChats(credentials: Credentials): Promise<Chat[]> {
    const baseUrl = await getBaseUrl();
    const res = await signedFetch(`${baseUrl}/chat/list`, JSON.stringify({json: {}}), credentials);
    const data = await res.json();
    const chats = data.json?.chats ?? data.json?.sessions ?? data.json?.items ?? [];
    return chats.map((c: any) => ({...c, id: c.chatId ?? c.id}));
}

export async function createChat(title: string, credentials: Credentials): Promise<Chat> {
    const baseUrl = await getBaseUrl();
    const res = await signedFetch(`${baseUrl}/chat/create`, JSON.stringify({json: {title}}), credentials);
    const data = await res.json();
    const chat = data.json;
    return {...chat, id: chat.chatId ?? chat.id};
}

export async function deleteChat(chatId: string, credentials: Credentials): Promise<void> {
    const baseUrl = await getBaseUrl();
    await signedFetch(`${baseUrl}/chat/delete`, JSON.stringify({json: {chatId}}), credentials);
}

export async function getMessages(chatId: string, credentials: Credentials): Promise<UIMessage[]> {
    const baseUrl = await getBaseUrl();
    const res = await signedFetch(`${baseUrl}/chat/getMessages`, JSON.stringify({json: {chatId}}), credentials);
    const data = await res.json();
    return data.json ?? [];
}

export class CassandraChatTransport implements ChatTransport<UIMessage> {
    private chatIdOverride: string | null = null;

    constructor(private credentials: Credentials) {}

    setChatId(chatId: string) {
        this.chatIdOverride = chatId;
    }

    async sendMessages(options: {
        chatId: string;
        messages: UIMessage[];
        abortSignal?: AbortSignal;
    }): Promise<ReadableStream<UIMessageChunk>> {
        const baseUrl = await getBaseUrl();
        const url = `${baseUrl}/chat/sendMessage`;
        const chatId = this.chatIdOverride ?? options.chatId;

        const lastMessage = options.messages[options.messages.length - 1];
        const isResumeFlow = lastMessage?.role === 'assistant';
        const messagesToSend = isResumeFlow ? [lastMessage] : options.messages.filter((m) => m.role === 'user').slice(-1);

        const body = JSON.stringify({
            json: {
                chatId,
                messages: messagesToSend,
            },
        });

        const res = await signedFetch(url, body, this.credentials, options.abortSignal);

        if (!res.ok) {
            throw new Error(`sendMessage failed: ${res.status} ${res.statusText}`);
        }

        return parseORPCStream(res);
    }

    async reconnectToStream(options: {chatId: string}): Promise<ReadableStream<UIMessageChunk> | null> {
        try {
            const baseUrl = await getBaseUrl();
            const url = `${baseUrl}/chat/resumeStream`;
            const body = JSON.stringify({json: {chatId: options.chatId}});
            const res = await signedFetch(url, body, this.credentials);

            if (!res.ok || !res.body) return null;

            return parseORPCStream(res);
        } catch {
            return null;
        }
    }
}
